import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { fileUploadService } from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import { FireflyImageEditorModal } from './FireflyImageEditorModal';
import {
	type FireflyEditWriteBackTarget,
	useFireflyEditWriteBack,
} from './useFireflyEditWriteBack';

interface UseFireflyEditModalResult {
	/** Open the editor for the given image + write-back target. */
	open: (imageUrl: string, target: FireflyEditWriteBackTarget) => void;
	/** Render this once in the host; it portals the modal when open. */
	modal: ReactNode;
}

/** Image MIME for the editor's content-type gate, from the URL extension. */
function imageMimeFromUrl(url: string): 'image/png' | 'image/jpeg' {
	const ext = url.split('?')[0].toLowerCase();
	if (ext.endsWith('.jpg') || ext.endsWith('.jpeg')) return 'image/jpeg';
	return 'image/png';
}

/**
 * Host-friendly controller for the Firefly editor (ADO-1412). A host component
 * (PostReview, DigitalAdArtifactPanel) renders its own `ActionButton` on the
 * existing hover rail and calls `open(imageUrl, target)`; it drops `modal` once
 * anywhere in its tree.
 *
 * IMAGE-IN: the editor opens an image by fetching a URL itself. A raw Azure blob
 * SAS URL is CROSS-ORIGIN and Azure does not send CORS headers, so the editor's
 * `fetch()` fails and it falls back to its empty state. So we pre-fetch the
 * source bytes through Bridgeway's ui-proxy (`getFileByUrl`, server-side fetch, no
 * CORS) and hand the editor a SAME-ORIGIN object URL it can fetch freely.
 */
export function useFireflyEditModal(): UseFireflyEditModalResult {
	const userId = useUserId();
	const writeBack = useFireflyEditWriteBack();
	const [state, setState] = useState<{
		sourceUrl: string;
		target: FireflyEditWriteBackTarget;
	} | null>(null);
	// Same-origin object URL the editor can fetch (null while loading / on error).
	const [objectUrl, setObjectUrl] = useState<string | null>(null);

	const open = useCallback(
		(imageUrl: string, target: FireflyEditWriteBackTarget) => {
			setState({ sourceUrl: imageUrl, target });
		},
		[],
	);

	// Pre-fetch the source image as a same-origin object URL whenever a new
	// edit session opens. Revoke the previous URL to avoid leaks.
	useEffect(() => {
		if (!state || !userId) {
			setObjectUrl(null);
			return;
		}
		let cancelled = false;
		let created: string | null = null;
		(async () => {
			try {
				const raw = await fileUploadService.getFileByUrl(
					state.sourceUrl,
					userId,
				);
				if (cancelled) return;
				// Azure serves these blobs as `application/octet-stream`. The editor
				// validates the fetched content-type against image/png|jpeg and
				// otherwise rejects the image ("Incorrect reference image file
				// type"), so re-type the blob to its real image MIME (from the URL
				// extension) before making the object URL.
				const mime = imageMimeFromUrl(state.sourceUrl);
				const typed = raw.type === mime ? raw : new Blob([raw], { type: mime });
				created = URL.createObjectURL(typed);
				setObjectUrl(created);
			} catch (err) {
				if (!cancelled) {
					logger.error('Failed to load source image for editor', err);
					setObjectUrl(null);
				}
			}
		})();
		return () => {
			cancelled = true;
			if (created) URL.revokeObjectURL(created);
		};
	}, [state, userId]);

	const handleClose = useCallback(() => {
		setState(null);
		setObjectUrl(null);
	}, []);

	const handleSave = useCallback(
		async (file: File) => {
			if (!state) return;
			await writeBack(file, state.target);
		},
		[writeBack, state],
	);

	const modal = useMemo(
		() => (
			<FireflyImageEditorModal
				isOpen={state !== null}
				imageUrl={objectUrl}
				saveBaseName={state?.target.artifactId}
				onClose={handleClose}
				onSave={handleSave}
				userId={userId}
			/>
		),
		[state, objectUrl, handleClose, handleSave, userId],
	);

	return { open, modal };
}
